import { useState } from "react";
import useSWR from "swr";

import SWR_KEYS from "@/constants/swr-keys";
import { FinancialTransaction } from "@/interfaces/talk2pay.iterfaces";
import t2payService from "@/services/t2pay.service";
import { RequestsBaseReturn } from "@/types/requests.types";

export type UseGetFinancialTransactionReturn = RequestsBaseReturn<FinancialTransaction | null> & {
  updateFinancialTransaction: (newStartDate: Date, newEndDate: Date) => void;
  isUpdating: boolean;
};

interface UseGetFinancialTransactionProps {
  startDate: Date;
  endDate: Date;
}

const useGetFinancialTransaction = ({
  startDate,
  endDate,
}: UseGetFinancialTransactionProps): UseGetFinancialTransactionReturn => {
  const key = [SWR_KEYS.getFinancialTransaction, startDate, endDate];

  const {
    data = null,
    isValidating: isLoading,
    error,
    mutate,
  } = useSWR<FinancialTransaction, Error>(
    key,
    ([, start, end]: [string, Date, Date]) =>
      t2payService.getFinancialTransaction({ startDate: start, endDate: end }),
    {
      revalidateOnFocus: false,
    }
  );
  const [isUpdating, setIsUpdating] = useState(false);

  const updateFinancialTransaction = async (newStartDate: Date, newEndDate: Date) => {
    setIsUpdating(true);
    try {
      await mutate(
        async () => {
          const updatedData = await t2payService.getFinancialTransaction({
            startDate: newStartDate,
            endDate: newEndDate,
          });
          return updatedData;
        },
        {
          revalidate: false,
        }
      );
    } finally {
      setIsUpdating(false);
    }
  };

  return { data, isLoading, error, updateFinancialTransaction, isUpdating };
};

export default useGetFinancialTransaction;
